import { Tooltip } from "./tooltips.js";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useDateFilter } from "./_40b_date_filter_context.js";
import { analyticsHandler } from "./eventLogger.js";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { TradeTagDisplay, BulkTagSection } from "./_88_trade_tag.js";

const MODULE_NAME = "_47_trade_tag_table";
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Debounce hook
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
}

// Helper function to convert date to MM/DD/YYYY format
function convertDateToMMDDYYYY(dateStr) {
    if (!dateStr) return "";
    if (/^\d{2}\/\d{2}\/\d{4}/.test(dateStr)) return dateStr;
    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        const [, yyyy, mm, dd] = isoMatch;
        return `${mm}/${dd}/${yyyy}`;
    }
    return dateStr;
}

// Helper function to format qty with thousand separators and no decimals
function formatQty(qty) {
    if (typeof qty !== 'number') qty = parseFloat(qty);
    return Math.round(qty).toLocaleString('en-US');
}

// Helper function to format profit/loss with thousand separators and 2 decimals
function formatPnL(pnl) {
    if (typeof pnl !== 'number') pnl = parseFloat(pnl);
    return pnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Helper function to format currency with $ symbol
function formatCurrency(value) {
    if (typeof value !== 'number') value = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
}

// Helper to format execution time with timezone conversion
function formatExecTime(execTimeStr) {
    if (!execTimeStr) return "";

    execTimeStr = String(execTimeStr);

    const match = execTimeStr.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}:\d{2}:\d{2}) ([A-Z]{3})$/);
    if (match) {
        const [_, mm, dd, yyyy, time, timezone] = match;

        const estOffset = {
            "PST": 3, "PDT": 3, "EST": 0, "EDT": 0, "CST": 1, "CDT": 1
        };

        let estTime = time;
        let estDate = `${mm}/${dd}/${yyyy}`;
        let conversionLabel = "";

        if (estOffset[timezone] !== undefined && estOffset[timezone] > 0) {
            const [hours, minutes, seconds] = time.split(":").map(Number);
            const estHours = hours + estOffset[timezone];

            let adjustedDate = new Date(`${yyyy}-${mm}-${dd}`);
            if (estHours >= 24) {
                adjustedDate.setDate(adjustedDate.getDate() + 1);
                estDate = `${String(adjustedDate.getMonth() + 1).padStart(2, "0")}/${String(adjustedDate.getDate()).padStart(2, "0")}/${adjustedDate.getFullYear()}`;
            }

            estTime = `${String(estHours % 24).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
            conversionLabel = `${timezone} → `;
        }

        const hoverTitle = conversionLabel
            ? `${conversionLabel}${estDate} ${estTime} EST`
            : `Already in EST`;

        return (
            <span title={hoverTitle} style={{ cursor: "help", borderBottom: "1px dotted #888" }}>
                {mm}/{dd}/{yyyy} {time} {timezone}
            </span>
        );
    }

    return execTimeStr;
}

// --- Gear Icon Component ---
const GearIcon = ({ size = 24, color = "#fff" }) => (
    <span
        style={{
            fontSize: size,
            color,
            verticalAlign: "middle",
            cursor: "pointer",
            userSelect: "none",
            lineHeight: 1,
            display: "inline-block",
        }}
        title="Settings"
        aria-label="Settings"
    >
        &#9881;
    </span>
);

// --- Toggle Switch Component with custom color support ---
function ToggleSwitch({ checked, onChange, label, description, accentColor = "#1976d2" }) {
    return (
        <div style={{ marginBottom: 16 }}>
            <label style={{
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
                width: "100%"
            }}>
                <span style={{ marginRight: 12, fontWeight: 600, minWidth: 200 }}>{label}</span>
                <span style={{
                    position: "relative",
                    display: "inline-block",
                    width: 44,
                    height: 24,
                    marginRight: 12
                }}>
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => onChange(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                        position: "absolute",
                        cursor: "pointer",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: checked ? accentColor : "#888",
                        borderRadius: 24,
                        transition: "background 0.2s"
                    }} />
                    <span style={{
                        position: "absolute",
                        left: checked ? 22 : 2,
                        top: 2,
                        width: 20,
                        height: 20,
                        background: "#fff",
                        borderRadius: "50%",
                        transition: "left 0.2s"
                    }} />
                </span>
            </label>
            {description && (
                <div style={{ color: "#aaa", fontSize: 11, marginTop: 4, marginLeft: 212 }}>
                    {description}
                </div>
            )}
        </div>
    );
}

function TradeTagTable({ onNavigate, screen, activeDate, refreshKey }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { navigationDate, setNavigationDate } = useDateFilter();

    // State management
    const [trades, setTrades] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [range, setRange] = useState("30");
    const [symbolFilter, setSymbolFilter] = useState("");
    const [posEffectFilter, setPosEffectFilter] = useState("ALL");
    const [execTimeFilter, setExecTimeFilter] = useState("");
    const [qtyFilter, setQtyFilter] = useState("");
    const [tooltip, setTooltip] = useState("");
    const [checkedRows, setCheckedRows] = useState({});
    const [allChecked, setAllChecked] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState(""); // "success" or "error"
    const [sortColumn, setSortColumn] = useState("exec_time");
    const [sortDirection, setSortDirection] = useState("desc");
    const [dateFilterFormat, setDateFilterFormat] = useState("MM/DD/YY or MM/DD/YYYY");
    const [symbolFilterFormat, setSymbolFilterFormat] = useState("Single or comma-separated");
    const [instrumentTypeFilter, setInstrumentTypeFilter] = useState("Stock");
    const [expandedGroups, setExpandedGroups] = useState({});
    const [groupExecutions, setGroupExecutions] = useState({});
    const [loadingExecutions, setLoadingExecutions] = useState({});
    const [showSettingsModal, setShowSettingsModal] = useState(false);

    // Debounce filter values to prevent excessive API calls
    const debouncedSymbolFilter = useDebounce(symbolFilter, 500);
    const debouncedQtyFilter = useDebounce(qtyFilter, 500);
    const debouncedExecTimeFilter = useDebounce(execTimeFilter, 300);

    // URL parameters
    const urlParams = new URLSearchParams(location.search);
    const urlSymbol = urlParams.get("symbol");
    const urlDate = urlParams.get("date");
    const urlQty = urlParams.get("qty");

    // Improved Initialization for Date
    useEffect(() => {
        const latestDate = sessionStorage.getItem("latest_date") || "";
        const storageDate = sessionStorage.getItem("navigationDate") || "";
        const dateToUse = urlDate || navigationDate || storageDate;

        if (dateToUse && dateToUse !== latestDate) {
            setExecTimeFilter(convertDateToMMDDYYYY(dateToUse));
        }
    }, []);

    // Sync state when URL or context changes
    useEffect(() => {
        const navSymbol = urlSymbol || sessionStorage.getItem("navigationSymbol") || "";
        if (navSymbol && navSymbol !== symbolFilter) {
            setSymbolFilter(navSymbol);
            setPage(1);
        }

        const navQty = urlQty || sessionStorage.getItem("navigationQty") || "";
        if (navQty && navQty !== qtyFilter) {
            setQtyFilter(navQty);
            setPage(1);
        }

        const latestDate = sessionStorage.getItem("latest_date") || "";
        const storageDate = sessionStorage.getItem("navigationDate") || "";
        const navDate = urlDate || navigationDate || storageDate;

        if (navDate && navDate !== latestDate) {
            const formattedDate = convertDateToMMDDYYYY(navDate);
            if (formattedDate !== execTimeFilter) {
                setExecTimeFilter(formattedDate);
                setPage(1);
            }
        }
    }, [location.search, navigationDate, urlSymbol, urlDate]);

    // Auto-set range to "ALL" when any filter is actively used
    useEffect(() => {
        const hasActiveFilters = debouncedExecTimeFilter || debouncedSymbolFilter || debouncedQtyFilter;
        if (hasActiveFilters && range !== "ALL") {
            setRange("ALL");
        }
    }, [debouncedExecTimeFilter, debouncedSymbolFilter, debouncedQtyFilter]);

    const getSortIndicator = (column) => {
        if (sortColumn !== column) return "";
        return sortDirection === "asc" ? " ↑" : " ↓";
    };

    const handleColumnSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    const handleExpandGroup = async (trade_group_id) => {
        const isCurrentlyExpanded = expandedGroups[trade_group_id];

        if (isCurrentlyExpanded) {
            setExpandedGroups(prev => ({
                ...prev,
                [trade_group_id]: false
            }));
            return;
        }

        if (groupExecutions[trade_group_id]) {
            setExpandedGroups(prev => ({
                ...prev,
                [trade_group_id]: true
            }));
            return;
        }

        setLoadingExecutions(prev => ({
            ...prev,
            [trade_group_id]: true
        }));

        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/trade-executions/${trade_group_id}`,
                { withCredentials: true }
            );

            if (res.data.success) {
                setGroupExecutions(prev => ({
                    ...prev,
                    [trade_group_id]: res.data.executions
                }));
                setExpandedGroups(prev => ({
                    ...prev,
                    [trade_group_id]: true
                }));
            }
        } catch (err) {
            console.error("Error fetching executions:", err);
            showMessage("Unable to load execution details", "error");
        } finally {
            setLoadingExecutions(prev => ({
                ...prev,
                [trade_group_id]: false
            }));
        }
    };

    // Message handler utility
    const showMessage = (msg, type = "success") => {
        setMessage(msg);
        setMessageType(type);
        // Auto-clear message after 5 seconds
        setTimeout(() => {
            setMessage("");
            setMessageType("");
        }, 5000);
    };

    // Main fetch useEffect
    useEffect(() => {
        let isCancelled = false;

        setLoading(true);

        // 🔓 NEW: Retrieve userKey from sessionStorage for decryption
        const userKey = sessionStorage.getItem("userKey");

        const fetchParams = {
            range,
            page,
            page_size: pageSize,
            symbol: debouncedSymbolFilter,
            exec_time: debouncedExecTimeFilter,
            qty: debouncedQtyFilter,
            instrument_type: instrumentTypeFilter.toUpperCase(),
            sort_column: sortColumn,
            sort_direction: sortDirection
        };

        // Inject userKey if it exists
        if (userKey) {
            fetchParams.userKey = userKey;
        }

        axios.get(`${process.env.REACT_APP_API_URL}/api/trade-tag-table`, {
            params: fetchParams, // Updated to use fetchParams with userKey support
            withCredentials: true
        }).then(res => {
            if (isCancelled) return;

            setTrades(res.data.trades);
            setTotalCount(res.data.total_count);
            setTooltip(res.data.tooltip);

            if (res.data.date_filter_format) {
                setDateFilterFormat(res.data.date_filter_format);
            }
            if (res.data.symbol_filter_format) {
                setSymbolFilterFormat(res.data.symbol_filter_format);
            }

            setLoading(false);
            setCheckedRows({});
            setAllChecked(false);
        }).catch((err) => {
            if (isCancelled) return;
            setLoading(false);
            if (err.response && err.response.status === 401) {
                showMessage("Authentication required. Please log in.", "error");
            } else {
                showMessage("Unable to load trades. Please try again.", "error");
            }
        });

        return () => { isCancelled = true; };
    }, [range, page, pageSize, debouncedSymbolFilter, debouncedExecTimeFilter, debouncedQtyFilter, instrumentTypeFilter, sortColumn, sortDirection]);

    // Filter change handlers
    const handleSymbolFilterChange = (e) => {
        setSymbolFilter(e.target.value);
        setPage(1);
    };

    const handlePosEffectFilterChange = (e) => {
        setPosEffectFilter(e.target.value);
        setPage(1);
    };

    const handleExecTimeFilterChange = (e) => {
        setExecTimeFilter(e.target.value);
        setPage(1);
    };

    const handleQtyFilterChange = (e) => {
        setQtyFilter(e.target.value);
        setPage(1);
    };

    const handleInstrumentTypeFilterChange = (e) => {
        setInstrumentTypeFilter(e.target.value);
        setPage(1);
    };

    const handleClearAllFilters = () => {
        const latestDate = sessionStorage.getItem("latest_date") || "";
        setRange("30");
        setPage(1);
        setSymbolFilter("");
        setPosEffectFilter("ALL");
        setExecTimeFilter("");
        setQtyFilter("");
        setInstrumentTypeFilter("Stock");
        setCheckedRows({});
        setAllChecked(false);
        sessionStorage.removeItem("navigationDate");
        sessionStorage.removeItem("navigationSymbol");
        if (setNavigationDate) setNavigationDate(latestDate);
        window.dispatchEvent(new CustomEvent("navigationDateChanged", { detail: { date: latestDate } }));
    };

    const renderSettingsModal = () => {
        if (!showSettingsModal) return null;
        return (
            <>
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(24,26,37,0.35)",
                        backdropFilter: "blur(1px)",
                        zIndex: 9998
                    }}
                    onClick={() => setShowSettingsModal(false)}
                />
                <div
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 9999,
                        minWidth: 500,
                        minHeight: 200,
                        background: "#181A25",
                        borderRadius: 12,
                        boxShadow: "0 2px 8px rgba(0,255,255,0.08)",
                        padding: 24
                    }}
                >
                    <h3 style={{ marginTop: 0, marginBottom: 20, color: "#fff" }}>Trade Table Settings</h3>
                    <p style={{ color: "#aaa", fontSize: 13 }}>Settings will be added here in future updates.</p>
                    <button
                        onClick={() => setShowSettingsModal(false)}
                        style={{
                            position: "absolute",
                            top: 12,
                            right: 18,
                            background: "none",
                            border: "none",
                            color: "#fff",
                            fontSize: 22,
                            cursor: "pointer"
                        }}
                    >
                        ✕
                    </button>
                </div>
            </>
        );
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    function getVisiblePages(page, totalPages) {
        const maxPages = 10;
        let start = Math.max(1, page - Math.floor(maxPages / 2));
        let end = start + maxPages - 1;
        if (end > totalPages) { end = totalPages; start = Math.max(1, end - maxPages + 1); }
        const pages = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    }

    const selectedTradeIds = Object.entries(checkedRows)
        .filter(([_, checked]) => checked)
        .map(([id]) => parseInt(id));

    const headerStyle = { padding: "8px 4px", textAlign: "left", cursor: "pointer", backgroundColor: "#444" };

    const handleRowCheck = (id) => {
        setCheckedRows(prev => ({ ...prev, [id]: !prev[id] }));
        setAllChecked(false);
    };

    const handleAllCheck = () => {
        if (allChecked) {
            setCheckedRows({});
            setAllChecked(false);
        } else {
            const newChecked = {};
            trades.forEach((trade) => {
                newChecked[trade.id] = true;
            });
            setCheckedRows(newChecked);
            setAllChecked(true);
        }
    };

    // Handlers for TradeTagDisplay callbacks
    const handleTagAdded = (id, newTag) => {
        setTrades(prevTrades =>
            prevTrades.map(trade => {
                if (trade.id === id) {
                    return {
                        ...trade,
                        tags: [...(trade.tags || []), newTag]
                    };
                }
                return trade;
            })
        );
    };

    const handleTagRemoved = (id, tag_id) => {
        setTrades(prevTrades =>
            prevTrades.map(trade => {
                if (trade.id === id) {
                    return {
                        ...trade,
                        tags: trade.tags.filter(tag => tag.id !== tag_id)
                    };
                }
                return trade;
            })
        );
    };

    const handleBulkTagsAdded = (data) => {
        const { tag_notes, trade_ids } = data;
        setTrades(prevTrades =>
            prevTrades.map(trade => {
                if (trade_ids.includes(trade.id)) {
                    return {
                        ...trade,
                        tags: [...(trade.tags || []), { tag_notes, id: Date.now() }]
                    };
                }
                return trade;
            })
        );
        setCheckedRows({});
        setAllChecked(false);
    };

    return (
        <div
            style={{ background: "#181A25", borderRadius: 8, padding: 16, margin: "0px 0" }}
            data-analytics={MODULE_NAME}
        >
            <h4
                style={{ marginTop: 0, marginBottom: 16, color: "#fff", fontSize: 18, fontWeight: 600 }}
            >
                🏷️ Stock/ETFs Table <Tooltip text={tooltip} />
            </h4>

            {/* Filters */}
            <div style={{ marginTop: 16, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <label>
                    Range:
                    <select
                        value={range}
                        onChange={e => setRange(e.target.value)}
                        style={{
                            marginLeft: 8,
                            background: !loading && totalCount === 0 ? "#e63946" : "#222",
                            color: "#fff",
                            border: !loading && totalCount === 0 ? "2px solid #ffd166" : "1px solid #444",
                            borderRadius: 4,
                            padding: 8,
                            fontWeight: !loading && totalCount === 0 ? "bold" : "normal",
                            boxShadow: !loading && totalCount === 0 ? "0 0 10px rgba(255, 209, 102, 0.6)" : "none",
                            transition: "all 0.3s ease"
                        }}
                    >
                        <option value="7">Last 7</option>
                        <option value="30">Last 30</option>
                        <option value="60">Last 60</option>
                        <option value="90">Last 90</option>
                        <option value="ALL">All Time</option>
                    </select>
                </label>
                <label>
                    Page Size:
                    <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ marginLeft: 8, background: "#222", color: "#fff", border: "1px solid #444", borderRadius: 4, padding: 8 }}>
                        {PAGE_SIZE_OPTIONS.map(sz => <option key={sz} value={sz}>{sz}</option>)}
                    </select>
                </label>

                <label>
                    Date:
                    <input
                        type="text"
                        value={execTimeFilter}
                        onChange={handleExecTimeFilterChange}
                        placeholder="08/21/2025"
                        style={{ marginLeft: 8, background: "#222", color: "#fff", border: "1px solid #444", borderRadius: 4, padding: 8, fontSize: 15, width: 160 }}
                        title={`Format: ${dateFilterFormat}`}
                    />
                </label>

                <label>
                    Symbol:
                    <input
                        type="text"
                        value={symbolFilter}
                        onChange={handleSymbolFilterChange}
                        placeholder="BTRS,HQRV"
                        style={{ marginLeft: 8, background: "#222", color: "#fff", border: "1px solid #444", borderRadius: 4, padding: 8, fontSize: 15, width: 140 }}
                        title={symbolFilterFormat}
                    />
                </label>

                <label>
                    Qty:
                    <input
                        type="number"
                        value={qtyFilter}
                        onChange={handleQtyFilterChange}
                        placeholder="100"
                        style={{ marginLeft: 8, background: "#222", color: "#fff", border: "1px solid #444", borderRadius: 4, padding: 8, fontSize: 15, width: 100 }}
                    />
                </label>

                <button
                    onClick={handleClearAllFilters}
                    style={{
                        background: "#444",
                        color: "#fff",
                        border: "1px solid #666",
                        borderRadius: 4,
                        padding: "8px 16px",
                        fontSize: 15,
                        cursor: "pointer"
                    }}
                >
                    Clear All
                </button>
                <Link
                    to="/pg_upload"
                    style={{
                        marginLeft: 16,
                        color: "#1976d2",
                        textDecoration: "none",
                        fontSize: 15,
                        cursor: "pointer"
                    }}
                    onClick={(e) => {
                        analyticsHandler({
                            event_type: "link_click",
                            module_name: MODULE_NAME,
                            subcomponent: "add-trades-link",
                            screen: screen,
                            metadata: {}
                        })(e);
                        if (onNavigate) {
                            onNavigate("pg_upload");
                        }
                    }}
                >
                    Add Trades
                </Link>
            </div>

            {/* Bulk Tag Section - Using New Component */}
            <BulkTagSection
                selectedTradeIds={selectedTradeIds}
                trades={trades}
                onTagsAdded={handleBulkTagsAdded}
                onMessage={showMessage}
            />

            {/* Trades table */}
            <div style={{ marginTop: 16, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", background: "#222" }}>
                    <thead>
                        <tr style={{ background: "#333", color: "#fff" }}>
                            <th
                                style={{ ...headerStyle, cursor: "pointer" }}
                                onClick={() => handleColumnSort("symbol")}
                            >
                                <input
                                    type="checkbox"
                                    checked={allChecked}
                                    onChange={handleAllCheck}
                                    style={{ marginRight: 8 }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                Symbol{getSortIndicator("symbol")}
                            </th>
                            <th
                                style={{ ...headerStyle, cursor: "pointer" }}
                                onClick={() => handleColumnSort("open_date")}
                            >
                                Open Date{getSortIndicator("open_date")}
                            </th>
                            <th
                                style={{ ...headerStyle, cursor: "pointer" }}
                                onClick={() => handleColumnSort("close_date")}
                            >
                                Close Date{getSortIndicator("close_date")}
                            </th>
                            <th
                                style={{ ...headerStyle, cursor: "pointer" }}
                                onClick={() => handleColumnSort("entry_price")}
                            >
                                Price{getSortIndicator("entry_price")}
                            </th>
                            <th
                                style={{ ...headerStyle, textAlign: "center", background: "#181A25", cursor: "pointer" }}
                                onClick={() => handleColumnSort("qty")}
                            >
                                Qty{getSortIndicator("qty")}
                            </th>
                            <th
                                style={{ ...headerStyle, textAlign: "center", background: "#181A25", cursor: "pointer" }}
                                onClick={() => handleColumnSort("profit_loss")}
                            >
                                P&L{getSortIndicator("profit_loss")}
                            </th>
                            <th style={headerStyle}>Risk %</th>
                            <th style={headerStyle}>Tags</th>
                        </tr>
                    </thead>
                    <tbody>
                        {trades.map((trade) => {
                            const isChecked = !!checkedRows[trade.id];
                            const isExpanded = expandedGroups[trade.id];
                            const isLoading = loadingExecutions[trade.id];
                            const executions = groupExecutions[trade.id] || [];

                            const borderStyle = isChecked ? {
                                border: "2px solid #e63946",
                                background: "#2d3540"
                            } : {};

                            return (
                                <React.Fragment key={trade.id}>
                                    {/* Trade Group Header Row */}
                                    <tr
                                        style={{ ...borderStyle, cursor: "pointer", fontWeight: "bold", background: "#2d3540", borderTop: "2px solid #444" }}
                                    >
                                        <td style={{ padding: "8px 4px", display: "flex", alignItems: "center", gap: "8px" }}>
                                            <span
                                                style={{ width: "20px", textAlign: "center", marginRight: "8px", cursor: "pointer" }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleExpandGroup(trade.id);
                                                }}
                                            >
                                                {isLoading ? "⟳" : (isExpanded ? "▼" : "▶")}
                                            </span>
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => handleRowCheck(trade.id)}
                                                style={{ marginRight: 4 }}
                                                onClick={e => e.stopPropagation()}
                                            />
                                            <span style={{ color: "#4caf50" }}>{trade.symbol}</span>
                                        </td>
                                        <td style={{ padding: "8px 4px" }}>{trade.open_date}</td>
                                        <td style={{ padding: "8px 4px" }}>{trade.close_date}</td>
                                        <td style={{ padding: "8px 4px" }}>
                                            ${Number(trade.entry_price || 0).toFixed(2)}
                                        </td>
                                        <td style={{ padding: "8px 4px", textAlign: "center", background: "#181A25" }}>
                                            {formatQty(trade.qty)}
                                        </td>
                                        <td style={{ padding: "8px 4px", textAlign: "center", fontWeight: "bold", background: "#181A25" }}>
                                            <span style={{ color: trade.profit_loss >= 0 ? "#228B22" : "#B22222" }}>
                                                {trade.profit_loss >= 0 ? "+" : ""}{formatPnL(trade.profit_loss)}
                                            </span>
                                        </td>
                                        <td style={{ padding: "8px 4px", fontSize: 12 }}>{trade.risk_percent.toFixed(2)}%</td>
                                        <td style={{ padding: "8px 4px", minWidth: "250px" }}>
                                            <TradeTagDisplay
                                                trade_group_id={trade.id}
                                                tags={trade.tags || []}
                                                onTagAdded={(newTag) => handleTagAdded(trade.id, newTag)}
                                                onTagRemoved={(tag_id) => handleTagRemoved(trade.id, tag_id)}
                                                onMessage={showMessage}
                                            />
                                        </td>
                                    </tr>

                                    {/* Expanded Executions Rows */}
                                    {isExpanded && executions.map((exec, idx) => (
                                        <tr
                                            key={`exec_${exec.id}`}
                                            style={{
                                                background: "#1a1d26",
                                                fontSize: 12,
                                                borderLeft: "3px solid #1976d2"
                                            }}
                                        >
                                            <td style={{ padding: "6px 4px", paddingLeft: 50 }}>
                                                {formatExecTime(exec.Exec_Time)}
                                            </td>
                                            <td style={{ padding: "6px 4px" }}>—</td>
                                            <td style={{ padding: "6px 4px" }}>{exec.Pos_effect}</td>
                                            <td style={{ padding: "6px 4px" }}>${Number(exec.price || 0).toFixed(2)}</td>
                                            <td style={{ padding: "6px 4px", textAlign: "center", background: "#0d0e12" }}>
                                                {formatQty(exec.Qty)}
                                            </td>
                                            <td style={{ padding: "6px 4px", textAlign: "center", background: "#0d0e12", fontWeight: "bold" }}>
                                                <span style={{ color: exec.profit_loss >= 0 ? "#228B22" : "#B22222" }}>
                                                    {exec.profit_loss > 0 ? "+" : ""}{formatPnL(exec.profit_loss)}
                                                </span>
                                            </td>
                                            <td style={{ padding: "6px 4px", textAlign: "right", color: "#999" }}>
                                                {exec["Risk %"].toFixed(2)}%
                                            </td>
                                            <td style={{ padding: "6px 4px" }}>—</td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div style={{ marginTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 14, gap: 12 }}>
                <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{ background: "#444", color: "#fff", border: "none", borderRadius: 4, padding: "6px 12px", cursor: "pointer" }}
                >
                    Prev
                </button>
                {getVisiblePages(page, totalPages).map(pn => (
                    <button
                        key={pn}
                        onClick={() => setPage(pn)}
                        style={{
                            background: pn === page ? "#e63946" : "#444",
                            color: "#fff",
                            border: "none",
                            borderRadius: 4,
                            padding: "6px 12px",
                            fontSize: 15,
                            cursor: "pointer",
                            fontWeight: pn === page ? "bold" : "normal"
                        }}
                    >
                        {pn}
                    </button>
                ))}
                <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{ background: "#444", color: "#fff", border: "none", borderRadius: 4, padding: "6px 16px", fontSize: 15, cursor: "pointer" }}
                >
                    Next
                </button>
            </div>

            {/* Summary Footer */}
            <div style={{ marginTop: 12, padding: 16, background: "#2d3540", borderRadius: 4, border: "1px solid #444" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
                    <div style={{ fontSize: 13, color: "#bbb", display: "flex", gap: 32 }}>
                        <span>
                            <strong style={{ color: "#4caf50" }}>{trades.length}</strong> trades shown
                        </span>
                        <span>
                            <strong style={{ color: "#1976d2" }}>{totalCount}</strong> total records
                        </span>
                    </div>

                    <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
                        <div style={{ fontSize: 15, fontWeight: "bold" }}>
                            <span
                                style={{
                                    color: trades.reduce((sum, t) => sum + t.profit_loss, 0) >= 0 ? "#228B22" : "#B22222"
                                }}
                            >
                                {formatCurrency(trades.reduce((sum, t) => sum + t.profit_loss, 0))}
                            </span>
                        </div>

                        <div style={{ fontSize: 12, color: "#999" }}>
                            Page {page} of {totalPages || 1}
                        </div>
                    </div>
                </div>
            </div>

            {/* Messages */}
            {message && (
                <div
                    style={{
                        marginTop: 16,
                        padding: 12,
                        borderRadius: 4,
                        color: messageType === "error" ? "#e63946" : "#228B22",
                        background: messageType === "error" ? "rgba(230, 57, 70, 0.1)" : "rgba(34, 139, 34, 0.1)",
                        border: `1px solid ${messageType === "error" ? "#e63946" : "#228B22"}`
                    }}
                >
                    {message}
                </div>
            )}

            {/* No results message */}
            {!loading && totalCount === 0 && (
                <div style={{
                    marginTop: 16,
                    padding: 12,
                    borderRadius: 4,
                    background: "#2d3540",
                    color: "#bbb",
                    fontSize: 14,
                    textAlign: "center",
                    border: "1px solid #444"
                }}>
                    No trades found. Try adjusting your filters.
                </div>
            )}

            {renderSettingsModal()}
        </div>
    );
}

export default TradeTagTable;